import React, {useEffect, useState} from 'react';
import {connect} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {CityApi, ConfigAddressApi, ConfigApi, LocationCheckApi} from "../../services/Api/types";
import {RootState} from "../../../reducers";
import ErrorFormView from "../Common/ErrorFormView";
import CitySearchComponent from "../Location/CitySearchComponent";
import ErrorFormAlertView from "../Common/ErrorFormAlertView";
import StreetSearchComponent from "../Location/StreetSearchComponent";
import {api} from "../../services/Api/api";
import ErrorFormHelper from "../../utils/ErrorFormHelper";
import Utils from "../../utils/Utils";

interface OwnProps {
    address: ConfigAddressApi,
    errors: any,
    config: ConfigApi,
    onChange: (value: any) => void,
    onErrors: (value: any) => void,
    handleOnCheckLocationAddress?: (data: LocationCheckApi) => void;
    forceCheckLocationAddressAtStart?: true;
    forceUpdateAddressFormValue?: boolean;
    setForceUpdateAddressFormValue?: React.Dispatch<React.SetStateAction<boolean>>;
    setForceStreet: React.Dispatch<React.SetStateAction<boolean>>;
    forceStreet: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

function NavbarLocationForm(props: Props) {
    const {t} = useTranslation();
    const [address, setAddress] = useState(props.address);
    const [errors, setErrors] = useState(props.errors);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
      const forceStreet = await checkAddressOnInit();

        if (props.forceCheckLocationAddressAtStart === true && address) {
            onCheckLocationAddress(address, typeof forceStreet === "boolean" ? forceStreet : undefined);
        }
    };

    useEffect(() => {
        if (props.forceUpdateAddressFormValue) {
            setAddress(props.address);
            setRefreshKey(key => key + 1);
            props.setForceUpdateAddressFormValue?.(false);
        }
    }, [props.forceUpdateAddressFormValue]);

    const checkAddressOnInit = async () => {
        if(!address.city) return;

        try {
           const res =  await api.getCities(address.city);
           const responseCity = res.find((city: CityApi) => city.city_name === address.city);

           if(responseCity && responseCity.has_streets) {
               props.setForceStreet(true);

               if(!address.street) {
                   handleStreetError();
               }

               return true;
           }
        }
        catch (err) {
            return null;
        }
    }

    const handleStreetError = () => {
        const errors = [{
            "field": "address.street",
            "code": "required"
        }];
        setErrors(errors);
        props.onErrors(errors);
    }

    const handleChangeStreet = (value: any, check?: any) => {
        let newAddress = {...address, street: value};
        setAddress(newAddress)
        props.onChange(newAddress)
        if (check) {
            onCheckLocationAddress(newAddress);
        }
        else {
            if(props.forceStreet && !value) {
                handleStreetError();
            }
        }
    }
    const handleChangeCity = (value: any, hasStreets: boolean, check?: any) => {
        if(!value) {
            props.setForceStreet(false);

            const requiredStreetErrorIndex = errors.findIndex((error: any) => error.field === "address.street" && error.code === "required");

            if(requiredStreetErrorIndex !== -1) {
                const newErrors = [...errors];
                newErrors.splice(requiredStreetErrorIndex, 1);
                setErrors(newErrors);
                props.onErrors(newErrors);
            }
        }
        else {
            props.setForceStreet(hasStreets);
        }

        let newAddress = {...address, city: value};
        setAddress(newAddress)
        props.onChange(newAddress)
        if (check) {
            onCheckLocationAddress(newAddress, hasStreets);
        }
    }
    const handleChangeBuildNr = (value: any) => {
        let newAddress = {...address, build_nr: value};
        setAddress(newAddress)
        props.onChange(newAddress)
    }
    const handleChangeFlatNr = (value: any) => {
        let newAddress = {...address, flat_nr: value};
        setAddress(newAddress)
        props.onChange(newAddress)
    }
    const onCheckLocationAddress = async (addressToCheck: any, hasStreets?: boolean) => {
        if(hasStreets !== undefined && hasStreets) {
            if (!addressToCheck.street) {
                handleStreetError();
                return;
            }
        }
        else {
            if(!addressToCheck.build_nr) return;
        }

        let addressData = {
            city: addressToCheck.city,
            street: addressToCheck.street,
            build_nr: addressToCheck.build_nr,
            flat_nr: addressToCheck.flat_nr,
            country: addressToCheck.country,
            zip_code: addressToCheck.zip_code,
        }
        try {
            let data = await api.getLocationCheck(addressData);
            if (props.handleOnCheckLocationAddress) {
                props.handleOnCheckLocationAddress(data);
            }

            if (!data.coordinates) {
                let errors = [{
                    "field": "address",
                    "code": "coordinates_not_exist"
                }];
                setErrors(errors);
                props.onErrors(errors);
            } else if (!data.in_delivery_area) {
                let errors = [{
                    "field": "address",
                    "code": "not_in_delivery_area"
                }];
                setErrors(errors);
                props.onErrors(errors);
            } else {
                setErrors([]);
                props.onErrors([]);
            }
        } catch (e) {
            setErrors([{
                "field": "address",
                "code": "connection_problem"
            }]);
        }

    }
    const onCheckLocation = async () => {
        onCheckLocationAddress(address)
    }
    const handleChangeCountry = (value: any) => {
        let newAddress = {...address, country: value};
        setAddress(newAddress)
        props.onChange(newAddress)
        onCheckLocationAddress(newAddress)
    }
    let countries = [];
    if (props.config.ordering.countries) {
        countries = Utils.countries(props.config.ordering.countries);
    }
    let isSelectCities = false;
    if (props.config.ordering.search_type === "SEARCH") {
        isSelectCities = true;
    }

    return (
        <div
            key={props.forceUpdateAddressFormValue === undefined ? "" : `address-form-updated-${refreshKey}`}>
            {(countries && countries.length > 1) ?
                <>
                    <div className="form-row row">
                        <div className="form-group col-3">
                            <select value={address.country} onChange={(e) => handleChangeCountry(e.currentTarget.value)}
                                    className={"form-control" + ErrorFormHelper(errors, "address.country")}
                                    id="country">
                                {countries.map((country: any) => (
                                    <option value={country.key} key={country.key}>{country.value}</option>
                                ))}
                            </select>
                            <label htmlFor="country">{t("common.address.country")}</label>
                            <ErrorFormView errors={errors} field="address.country"/>
                        </div>
                        <div className={`form-group col-9${address.city ? " focus" : ""}`}>
                            {isSelectCities ?
                                <CitySearchComponent placeholder={t("common.address.city") + "*"}
                                                     onBlur={(value: any, _: any, hasStreets: boolean) => handleChangeCity(value, hasStreets, true)}
                                                     value={address.city} zip={address.zip_code}
                                                     onChange={(value: any, _: any, hasStreets: boolean) => handleChangeCity(value, hasStreets )}/> :
                                <input type="text" value={address.city} onBlur={onCheckLocation}
                                       onChange={(e) => handleChangeCity(e.currentTarget.value, true)}
                                       className={"form-control" + ErrorFormHelper(errors, "address.city")} id="city"
                                       placeholder={t("common.address.city") + "*"}/>
                            }
                            <label htmlFor="city">{t("common.address.city")}</label>
                            <ErrorFormView errors={errors} field="address.city"/>
                        </div>
                    </div>
                </> :
                <>
                    <div className={`form-group${address.city ? " focus" : ""}`}>
                        {isSelectCities ?
                            <CitySearchComponent placeholder={t("common.address.city") + "*"}
                                                 onBlur={(value: any, _: any, hasStreets: boolean) => handleChangeCity(value, hasStreets, true)}
                                                 value={address.city} zip={address.zip_code}
                                                 onChange={(value: any, _: any, hasStreets: boolean) => handleChangeCity(value, hasStreets )}/> :
                            <input type="text" value={address.city} onBlur={onCheckLocation}
                                   onChange={(e) => handleChangeCity(e.currentTarget.value, true)}
                                   className={"form-control" + ErrorFormHelper(errors, "address.city")} id="city"
                                   placeholder={t("common.address.city") + "*"}/>
                        }
                        <label htmlFor="city">{t("common.address.city")}</label>
                        <ErrorFormView errors={errors} field="address.city"/>
                    </div>
                </>
            }
            <div className="form-row row">
                <div className={`form-group col-12 col-sm-6${address.street ? " focus" : ""}`}>
                    {isSelectCities ?
                        <StreetSearchComponent  placeholder={`${t("common.address.street")}${props.forceStreet ? "*" : ""}`} city={address.city}
                                               onBlur={(value: any) => handleChangeStreet(value, true)}
                                               value={address.street} className={ErrorFormHelper(errors, "address.street")}
                                               onChange={(value: any) => handleChangeStreet(value)}/> :
                        <input type="text" value={address.street} onBlur={onCheckLocation}
                               onChange={(e) => handleChangeStreet(e.currentTarget.value)}
                               className={"form-control" + ErrorFormHelper(errors, "address.street")} id="street"
                               placeholder={`${t("common.address.street")}${props.forceStreet ? "*" : ""}`}/>
                    }
                    <label htmlFor="street">{t("common.address.street")}</label>
                    <ErrorFormView errors={errors} field="address.street"/>
                </div>
                <div className="form-group col-6 col-sm-3">
                    <input type="text" value={address.build_nr} onBlur={onCheckLocation}
                           onChange={(e) => handleChangeBuildNr(e.currentTarget.value)}
                           className={"form-control" + ErrorFormHelper(errors, "address.build_nr")} id="build_nr"
                           placeholder={t("common.address.number") + "*"}/>
                    <label htmlFor="build_nr">{t("common.address.number")}</label>
                    <ErrorFormView errors={errors} field="address.build_nr"/>
                </div>
                <div className="form-group col-6 col-sm-3">
                    <input type="text" value={address.flat_nr}
                           onChange={(e) => handleChangeFlatNr(e.currentTarget.value)}
                           className={"form-control" + ErrorFormHelper(errors, "address.flat_nr")} id="flat_nr"
                           placeholder={t("common.address.flat_nr") + ""}/>
                    <label htmlFor="flat_nr">{t("common.address.flat_nr")}</label>
                    <ErrorFormView errors={errors} field="address.flat_nr"/>
                </div>
            </div>
            <ErrorFormAlertView errors={errors} field="address"/>
        </div>
    )
}

const mapStateToProps = (state: RootState) => {
    return {}
}
export default withTranslation()(connect(mapStateToProps)(NavbarLocationForm))
